import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  _getAssignmentsCount,
  _getUserLeadAssignments,
  _updateSubStatus,
  _getSchoolsAssigned,
  _createAssignLead,
} from 'controllers/lead-assignments/lead-assignments-controller';
import { useUpdateLead } from './useUpdateLead';
import { _sendApplications } from 'controllers/leads/leads_controller';

/**
 * @typedef {Object} LeadAssignmentsParams
 * @property {string} user_id
 * @property {string} status
 * @property {string} createdAt_from
 * @property {string} createdAt_to
 * @property {Array<string>} grades
 * @property {Array<string>} sources
 * @property {string} search_notes
 * @property {boolean} assigned_schools
 */
export const useLeadAssignments = ({
  user_id,
  status,
  createdAt_from,
  createdAt_to,
  grades,
  sources,
  search_notes,
  assigned_schools,
  school_id,
  year,
  language,
  lead_source_id,
  lead_status_id,
  textSearch,
  page,
  pageSize,
  fieldSort,
  fieldDirection,
}) => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchLeadAssignments = async () => {
    if (status === '') return;

    setLoading(true);
    setError(null);
    try {
      const response = await _getUserLeadAssignments({
        user_id,
        status,
        createdAt_from,
        createdAt_to,
        grades,
        sources,
        search_notes,
        assigned_schools,
        school_id,
        year,
        language,
        lead_source_id,
        lead_status_id,
        textSearch,
        page,
        pageSize,
        fieldSort,
        fieldDirection,
      });
      const data = await response.json();
      setData(data || {});
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeadAssignments();
  }, [
    status,
    createdAt_from,
    createdAt_to,
    grades,
    search_notes,
    assigned_schools,
    school_id,
    year,
    language,
    lead_source_id,
    lead_status_id,
    textSearch,
    page,
    pageSize,
    fieldSort,
    fieldDirection,
  ]);

  const { selectedLead, selectedLeads } = useSelector((state) => state.spot);
  const { updateLead, loading: leadLoading } = useUpdateLead();

  const updateAssignment = async ({ user_id, newValue, archivedReason, acceptedYear }) => {
    setLoading(true);
    setError(null);
    const leadIds = selectedLeads.length > 0 ? selectedLeads : [selectedLead];

    // const isSubStatusUpdate = ['attempting-contact', 'working', 'matched'].includes(newValue);
    const leadsGroupedBySchool = getGroupedLeads(leadIds);

    try {
      switch (newValue) {
        case 'new':
          console.log('STATUS: new, sub_status: empty');

          // update assignment
          for (const lead of leadIds) {
            if (!lead.lead_assignment_id) {
              await _createAssignLead({
                assignedUserId: user_id,
                leadId: lead.id,
                subStatus: '',
                assignmentType: 'school_assigned',
              });
            } else {
              await _updateSubStatus({
                user_id,
                assignment_id: lead.lead_assignment_id,
                sub_status: '',
              });
            }
          }
          // update lead
          for (const school of leadsGroupedBySchool) {
            await updateLead({ schoolId: school.schoolId, leadIds: school.leads, values: { status: 'new' } });
          }
          break;

        case 'attempting-contact':
        case 'working':
        case 'matched':
          console.log(`STATUS: new, sub_status: ${newValue}`);
          for (const lead of leadIds) {
            if (!lead.lead_assignment_id) {
              await _createAssignLead({
                assignedUserId: user_id,
                leadId: lead.id,
                subStatus: newValue,
                assignmentType: 'school_assigned',
              });
            } else {
              await _updateSubStatus({
                user_id,
                assignment_id: lead.lead_assignment_id,
                sub_status: newValue,
              });
            }
          }

          for (const school of leadsGroupedBySchool) {
            await updateLead({ schoolId: school.schoolId, leadIds: school.leads, values: { status: 'new' } });
          }
          break;

        default:
          console.log(`STATUS: ${newValue}, sub_status: empty`);
          for (const lead of leadIds) {
            if (lead.lead_assignment_id) {
              await _updateSubStatus({
                user_id,
                assignment_id: lead.lead_assignment_id,
                sub_status: '',
              });
            }
          }

          // update lead
          for (const school of leadsGroupedBySchool) {
            let payload = { status: newValue };
            if (newValue === 'archived' && archivedReason) {
              payload.reason_id = archivedReason;
            }
            if (newValue === 'accepted' && acceptedYear) {
              payload.year_accepted = acceptedYear;
            }

            await updateLead({ schoolId: school.schoolId, leadIds: school.leads, values: payload });
          }
          break;
      }

      return true;
    } catch (err) {
      setError(err);
      return false;
    } finally {
      await fetchLeadAssignments();
      setLoading(false);
    }
  };

  const handleSentApp = async ({ schoolId, leadId, userId }) => {
    setLoading(true);
    setError(null);

    try {
      const res = await _sendApplications(schoolId, [leadId], true, userId);
      const data = await res.json();
      if (data.length > 0) {
        fetchLeadAssignments();
        return true;
      }
    } catch (error) {
      console.log('handleSentApp', error);
      setError(error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    data,
    loading,
    error,
    getLeadAssignments: fetchLeadAssignments,
    updateAssignment,
    handleSentApp,
  };
};

const getGroupedLeads = (leadIds) => {
  const leadsFromSchoolMap = {};
  for (const lead of leadIds) {
    if (!leadsFromSchoolMap[lead.school_id]) {
      leadsFromSchoolMap[lead.school_id] = [];
    }
    leadsFromSchoolMap[lead.school_id].push(lead.id);
  }
  return Object.entries(leadsFromSchoolMap).map(([schoolId, leads]) => ({
    schoolId: Number(schoolId),
    leads,
  }));
};
